<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { Filters } from "~/core/models/filters";
import { PATH_CAMPAIGNS_UID } from "~/modules/campaign/constants";
import { CAMPAIGN_PARAMS_SCOPE } from "~/modules/campaign/constants/constants";
import { hasOperationModelEnabledInLicense } from "~/modules/campaign/utils/hasOperationModelEnabledInLicense";
import { useFetchDtwinOperationModels } from "~/modules/dtwin/composables/useFetchDtwinOperationModels";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcPlus from "~icons/lxc/plus";

const { t } = useI18n();

const router = useRouter();
const acl = useAcl();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  fetchAllData: fetchDtwinOperationModels,
  allResults: dtwinOperationModelsResults,
  setFilter: setDtwinOperationModelsFilter,
} = useFetchDtwinOperationModels({
  organizationCode: currentOrganization.value?.code,
  searchMode: SearchMode.FILTER_SEARCH,
});

/** Check if the license authorizes at least one operation model */
const hasOperationModelLicensed = computed(() =>
  dtwinOperationModelsResults.value
    ? hasOperationModelEnabledInLicense(dtwinOperationModelsResults.value)
    : false,
);

const canManageCampaign = computed(() => {
  return (
    acl.can(ACLRoles.CAN_READ_DEVICE) &&
    dtwinOperationModelsResults.value &&
    dtwinOperationModelsResults.value.length > 0 &&
    hasOperationModelLicensed.value
  );
});

const showCampaignOperationMgrFunnel: Ref<boolean> = ref(false);
function showNewCampaignFunnel() {
  showCampaignOperationMgrFunnel.value = true;
}

onMounted(async () => {
  setDtwinOperationModelsFilter(Filters.DTWIN_OPERATION_MODEL_HIDDEN, "false");
  const params = new Map();
  params.set(CAMPAIGN_PARAMS_SCOPE, true);
  await fetchDtwinOperationModels(params);
});
</script>

<template>
  <page-component check-license>
    <template #top-right>
      <lxc-button
        v-if="canManageCampaign"
        :icon="ILxcPlus"
        class="h-11"
        size="large"
        type="primary"
        :title="t('campaign.newCampaign')"
        @click="showNewCampaignFunnel"
      >
        {{ t("campaign.newCampaign") }}
      </lxc-button>
      <campaign-operation-mgr-funnel
        v-model:show="showCampaignOperationMgrFunnel"
        @saved="
          (uid) => router.push({ name: PATH_CAMPAIGNS_UID, params: { uid } })
        "
      />
    </template>
    <campaign-operation-mgr-list />
  </page-component>
</template>
